<template>
  <div class="row">
    <div class="col-xl-12">
      <div class="kt-portlet kt-portlet--mobile">
        <!-- Header -->
        <div class="kt-portlet__head kt-portlet__head--lg">
          <div class="kt-portlet__head-label">
            <span class="kt-portlet__head-icon">
              <i class="fas fa-clipboard-list"></i>
            </span>
            <h3 class="kt-portlet__head-title">
              Stock Cover Days Report
            </h3>
          </div>
          <!-- R2-29: ตัดข้อความ "ข้อมูล ณ วันที่ …" ที่มุมขวาบนออก (ไฟล์ Export ยังคงระบุวันที่ไว้) -->
          <div class="kt-portlet__head-toolbar"></div>
        </div>

        <div class="kt-portlet__body">
          <!-- ── FILTER BAR (§4 — 6 ช่อง · บังคับ 4: คลัง/เดือน/ปี/ชื่อสินค้า) ── -->
          <form class="kt-form" @submit.prevent="loadReport">
            <!-- แถว 1: คลังสินค้า · ชื่อสินค้า · แบรนด์สินค้า -->
            <div class="row">
              <div class="col-xl-4 col-lg-4 col-md-6">
                <div class="form-group">
                  <label class="label-required">คลังสินค้า:</label>
                  <select2
                    :css-style="'width: 100%;'"
                    :css-class="'form-control m-select2'"
                    :id="'rptPlantCodes'"
                    :name="'rptPlantCodes'"
                    :select2-options="plantOptions"
                    v-model="filterPlantCodes"
                    ref="rptPlantCodesSelect2"
                    multiple
                  ></select2>
                </div>
              </div>

              <div class="col-xl-4 col-lg-4 col-md-6">
                <div class="form-group">
                  <label class="label-required">ชื่อสินค้า:</label>
                  <select2
                    :css-style="'width: 100%;'"
                    :css-class="'form-control m-select2'"
                    :id="'rptProductCodes'"
                    :name="'rptProductCodes'"
                    :select2-options="productOptions"
                    v-model="filterProductCodes"
                    ref="rptProductCodesSelect2"
                    multiple
                  ></select2>
                </div>
              </div>

              <div class="col-xl-4 col-lg-4 col-md-6">
                <div class="form-group">
                  <label>แบรนด์สินค้า:</label>
                  <select2
                    :css-style="'width: 100%;'"
                    :css-class="'form-control m-select2'"
                    :id="'rptBrandIds'"
                    :name="'rptBrandIds'"
                    :select2-options="brandOptions"
                    v-model="filterBrandIds"
                    ref="rptBrandIdsSelect2"
                    multiple
                  ></select2>
                </div>
              </div>
            </div>

            <!-- แถว 2: เดือน · ปี · หน่วย -->
            <div class="row">
              <div class="col-xl-4 col-lg-4 col-md-6">
                <div class="form-group">
                  <label class="label-required">เดือน:</label>
                  <select class="form-control" v-model.number="filterMonth">
                    <option v-for="(lbl, i) in monthLabels" :key="'m-' + i" :value="i + 1">{{ lbl }}</option>
                  </select>
                </div>
              </div>

              <div class="col-xl-4 col-lg-4 col-md-6">
                <div class="form-group">
                  <label class="label-required">ปี:</label>
                  <select class="form-control" v-model.number="filterYear">
                    <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
                  </select>
                </div>
              </div>

              <div class="col-xl-4 col-lg-4 col-md-6">
                <div class="form-group">
                  <label>หน่วย:</label>
                  <select class="form-control" v-model="filterUnit">
                    <option value="ลิตร">ลิตร</option>
                    <option value="ถัง">ถัง</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-12 d-flex align-items-center">
                <button type="submit" class="btn btn-primary">
                  <i class="fas fa-search"></i> ค้นหา
                </button>
                <button type="button" class="btn btn-secondary ml-2" @click="resetFilter">
                  ล้างค่าทั้งหมด
                </button>
                <!-- R2-21: ย้าย Export Excel มาแถวเดียวกับค้นหา · ml-auto = ชิดขวาสุด -->
                <button
                  type="button"
                  class="btn btn-outline-primary ml-auto"
                  @click="exportExcel"
                  :disabled="isExporting || !report"
                >
                  <i :class="isExporting ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'"></i>
                  {{ isExporting ? 'กำลังดาวน์โหลด...' : 'Export Excel' }}
                </button>
              </div>
            </div>
          </form>

          <hr />

          <!-- Loading / empty -->
          <div v-if="isLoading" class="text-center py-5">
            <div class="kt-spinner kt-spinner--lg kt-spinner--brand"></div>
            <div class="mt-3">กำลังโหลดข้อมูล...</div>
          </div>

          <div v-else-if="!report" class="text-center text-muted py-4">
            เลือกตัวกรอง แล้วกดปุ่ม "ค้นหา" เพื่อดูรายงาน
          </div>

          <div v-else>
            <div v-if="report.isMtd" class="alert alert-info py-2" role="alert">
              <i class="fas fa-info-circle"></i>
              เดือนปัจจุบัน = <strong>MTD</strong> (Avg Sales/วัน ใช้จำนวนวันขายที่ผ่านมา ณ วันที่เรียกดู)
            </div>
            <!-- P4a: Monthly Snapshot — selected month is CLOSED (frozen snapshot) -->
            <div v-if="report.isClosed" class="alert py-2" role="alert"
                 style="background:#e0e7ff; color:#3730a3; border:1px solid #c7d2fe;">
              🔒 <strong>ปิดรอบเดือนข้อมูลแล้วไม่สามารถแก้ไขข้อมูลได้</strong> - การแก้ไข/ทดลองเกณฑ์จะไม่กระทบเปลี่ยนแปลงข้อมูลเดือนนี้
            </div>

            <!-- ── 11-COLUMN FLAT TABLE (§2/§3) ────────────────────── -->
            <div class="table-responsive">
              <table class="table table-bordered table-sm scd-report mb-0">
                <thead>
                  <tr>
                    <th class="text-center">เดือน</th>
                    <th class="text-center">ปี</th>
                    <th>คลังสินค้า</th>
                    <th class="text-center">รหัสสินค้า</th>
                    <th>ชื่อสินค้า</th>
                    <th>แบรนด์สินค้า</th>
                    <th class="text-right">Stock คงเหลือ ({{ report.unit || filterUnit }})</th>
                    <th class="text-right">Avg Sales/วัน</th>
                    <th class="text-center">Stock Cover Days</th>
                    <th>สถานะสต็อก</th>
                    <th>แนวทางแก้ไข</th>
                  </tr>
                </thead>
                <tbody v-if="report.groups.length === 0">
                  <tr>
                    <td colspan="11" class="text-center text-muted py-3">
                      ไม่พบข้อมูลตามเงื่อนไขที่เลือก
                    </td>
                  </tr>
                </tbody>
                <tbody v-for="(group, gi) in report.groups" :key="'g-' + gi">
                  <tr v-for="(r, ri) in group.rows" :key="'g-' + gi + '-r-' + ri">
                    <td class="text-center">{{ r.monthLabel }}</td>
                    <td class="text-center">{{ r.year }}</td>
                    <td>[{{ r.plantCode }}] {{ r.plantName }}</td>
                    <td class="text-center">{{ r.productCode }}</td>
                    <td>{{ r.productName }}</td>
                    <td>{{ r.brandName }}</td>
                    <!-- G — Stock คงเหลือ (rolling BG) -->
                    <td class="text-right">{{ formatNum(r.stockRemaining) }}</td>
                    <!-- H — Avg Sales/วัน -->
                    <td class="text-right">{{ formatNum(r.avgSalesPerDay) }}</td>
                    <!-- I — SCVD -->
                    <td class="text-center">{{ formatScvd(r.scvd) }}</td>
                    <!-- J — สถานะสต็อก (สีตามระดับเกณฑ์) -->
                    <td class="text-center scd-status-cell" :style="statusStyle(r)">{{ r.status }}</td>
                    <!-- K — แนวทางแก้ไข (สีตัวอักษรตามระดับเกณฑ์ เดียวกับ J · ไม่มีพื้นหลัง) -->
                    <td :style="actionStyle(r)">{{ r.actionText }}</td>
                  </tr>
                  <!-- Total ต่อคลัง (§7) -->
                  <tr class="scd-total-row">
                    <td colspan="6" class="text-right">
                      Total [{{ group.plantCode }}] {{ group.plantName }}
                    </td>
                    <td class="text-right">{{ formatNum(group.total.stockRemaining) }}</td>
                    <td class="text-right">{{ formatNum(group.total.avgSalesPerDay) }}</td>
                    <td colspan="3"></td>
                  </tr>
                </tbody>
                <tfoot v-if="report.groups.length > 0">
                  <tr class="scd-grand-total-row">
                    <td colspan="6" class="text-right">Grand Total</td>
                    <td class="text-right">{{ formatNum(report.grandTotal.stockRemaining) }}</td>
                    <td class="text-right">{{ formatNum(report.grandTotal.avgSalesPerDay) }}</td>
                    <td colspan="3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import select2 from "../../Shared/Select2";

require("sweetalert2");

export default {
  components: {
    select2,
  },
  data: function () {
    var self = this;
    var now = new Date();
    var currentYear = now.getFullYear();
    var authTransport = function (params, success, failure) {
      params.beforeSend = function (request) {
        request.setRequestHeader(
          "Authorization",
          "Bearer " + self.$root.user.access_token
        );
      };
      var $request = $.ajax(params);
      $request.then(success);
      $request.fail(failure);
      return $request;
    };

    return {
      isLoading: false,
      isExporting: false,
      report: null,

      // ── Filter state (§4) ─────────────────────────────────────────
      filterPlantCodes: [],
      filterProductCodes: [],
      filterBrandIds: [],
      filterUnit: "ลิตร",
      filterMonth: now.getMonth() + 1, // default = เดือนปัจจุบัน
      filterYear: currentYear, // default = ปีปัจจุบัน

      monthLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
      // ปี: ย้อนหลังรวมปัจจุบัน 5 ปี (§4)
      yearOptions: [
        currentYear,
        currentYear - 1,
        currentYear - 2,
        currentYear - 3,
        currentYear - 4,
      ],

      // Plant Select2 — reuse the scoped endpoint the Master/Report Analysis pages use (F6)
      plantOptions: {
        placeholder: "เลือกคลัง",
        allowClear: true,
        ajax: {
          url: "/Api/StockCoverDayTarget/Select2Plant",
          type: "post",
          delay: 300,
          dataType: "json",
          contentType: "application/json",
          data: function (params) {
            return JSON.stringify({
              plantId: [],
              search: params.term || "",
              page: params.page || 1,
            });
          },
          processResults: function (data, params) {
            var results = data.results || [];
            // CR-SCVD #45(D): prepend a client-side "ทั้งหมด" option on the first
            // page (backend NormalizeSelection treats "ทั้งหมด" as "all คลัง").
            var term = (params && params.term ? params.term : "").toLowerCase();
            var firstPage = !params || !params.page || params.page === 1;
            if (firstPage && (term === "" || "ทั้งหมด".indexOf(term) !== -1)) {
              results = [{ id: "ทั้งหมด", text: "ทั้งหมด" }].concat(results);
            }
            return { results: results, pagination: { more: data.more } };
          },
          transport: authTransport,
        },
        language: { noResults: function () { return "ไม่พบข้อมูล Plant"; } },
      },

      // Product Select2 — reuse the same endpoint (สินค้าสำเร็จรูป น้ำเบียร์) as the Master/RA pages
      productOptions: {
        placeholder: "เลือกสินค้า",
        allowClear: true,
        ajax: {
          url: "/Api/Product/Select2Product",
          delay: 300,
          data: function (params) {
            return {
              productGroupId: 14,
              productTypeId: 22,
              onlyStockCoverDays: true, // CR-StockCoverDays A3: เฉพาะ SKU ที่เปิดใช้งาน SCVD
              onlyHasScvdMaster: true, // CR-StockCoverDays C3: แสดงเฉพาะสินค้าที่มีข้อมูล Master (StockCoverDay_Target)
              search: params.term || "",
              page: params.page || 1,
            };
          },
          processResults: function (data, params) {
            var results = data.results || [];
            // CR-SCVD #45(D): prepend a client-side "ทั้งหมด" option on the first
            // page (backend NormalizeSelection treats "ทั้งหมด" as "all สินค้า").
            var term = (params && params.term ? params.term : "").toLowerCase();
            var firstPage = !params || !params.page || params.page === 1;
            if (firstPage && (term === "" || "ทั้งหมด".indexOf(term) !== -1)) {
              results = [{ id: "ทั้งหมด", text: "ทั้งหมด" }].concat(results);
            }
            return { results: results, pagination: { more: data.more } };
          },
          transport: authTransport,
        },
        language: { noResults: function () { return "ไม่พบข้อมูลสินค้า"; } },
      },

      // Brand Select2 — reuse the shared /Api/ProductBrand/Select2ProductBrand endpoint
      brandOptions: {
        placeholder: "ทั้งหมด",
        allowClear: true,
        ajax: {
          url: "/Api/ProductBrand/Select2ProductBrand",
          delay: 300,
          data: function (params) {
            return {
              search: params.term || "",
              page: params.page || 1,
            };
          },
          processResults: function (data) {
            return { results: data.results, pagination: { more: data.more } };
          },
          transport: authTransport,
        },
        language: { noResults: function () { return "ไม่พบข้อมูลแบรนด์"; } },
      },
    };
  },
  methods: {
    buildPayload: function () {
      return {
        PlantCodes: this.filterPlantCodes || [],
        ProductCodes: this.filterProductCodes || [],
        BrandIds: this.filterBrandIds || [],
        Unit: this.filterUnit,
        Month: parseInt(this.filterMonth, 10),
        Year: parseInt(this.filterYear, 10),
      };
    },

    // §4 บังคับ 4: คลัง / เดือน / ปี / ชื่อสินค้า (เดือน+ปี มี default อยู่แล้ว)
    validateRequired: function () {
      if (!this.filterPlantCodes || this.filterPlantCodes.length === 0) {
        Swal.fire("กรุณาเลือกตัวกรอง", "กรุณาเลือกคลังสินค้า", "warning");
        return false;
      }
      if (!this.filterProductCodes || this.filterProductCodes.length === 0) {
        Swal.fire("กรุณาเลือกตัวกรอง", "กรุณาเลือกชื่อสินค้า", "warning");
        return false;
      }
      return true;
    },

    loadReport: function () {
      var self = this;
      if (!self.validateRequired()) return;
      self.isLoading = true;
      self.report = null;
      axios
        .post("/Api/StockCoverDayTarget/Report/Search", self.buildPayload(), {
          headers: { Authorization: "Bearer " + self.$root.user.access_token },
        })
        .then(function (response) {
          if (response.data && response.data.success && response.data.data) {
            var d = response.data.data;
            d.groups = d.groups || [];
            d.grandTotal = d.grandTotal || { stockRemaining: 0, avgSalesPerDay: 0 };
            self.report = d;
          } else {
            Swal.fire("เกิดข้อผิดพลาด", (response.data && response.data.message) || "ไม่สามารถโหลดข้อมูลได้", "error");
          }
        })
        .catch(function (error) {
          var message = "เกิดข้อผิดพลาดในการโหลดข้อมูล";
          if (error.response && error.response.data && error.response.data.message) {
            message = error.response.data.message;
          }
          Swal.fire("เกิดข้อผิดพลาด", message, "error");
        })
        .then(function () {
          self.isLoading = false;
        });
    },

    resetFilter: function () {
      var now = new Date();
      this.filterPlantCodes = [];
      this.filterProductCodes = [];
      this.filterBrandIds = [];
      this.filterUnit = "ลิตร";
      this.filterMonth = now.getMonth() + 1;
      this.filterYear = now.getFullYear();
      $("#rptPlantCodes").val(null).trigger("change");
      $("#rptProductCodes").val(null).trigger("change");
      $("#rptBrandIds").val(null).trigger("change");
      this.report = null;
    },

    exportExcel: function () {
      var self = this;
      if (!self.validateRequired()) return;
      self.isExporting = true;
      KTApp.blockPage({ opacity: 0.05, message: "กำลังสร้างไฟล์ กรุณารอสักครู่..." });
      axios({
        method: "post",
        url: "/Api/StockCoverDayTarget/Report/Export",
        responseType: "arraybuffer",
        data: self.buildPayload(),
        headers: { Authorization: "Bearer " + self.$root.user.access_token },
      })
        .then(function (response) {
          if (response.status === 200) {
            var contentType = response.headers["content-type"];
            var disposition = response.headers["content-disposition"];
            var filename = "StockCoverDays_Report.xlsx";
            if (disposition && disposition.indexOf("attachment") !== -1) {
              var m = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
              if (m != null && m[1]) filename = decodeURIComponent(m[1].replace(/['"]/g, ""));
            }
            var blob = new Blob([response.data], { type: contentType });
            var link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } else if (response.status === 204) {
            Swal.fire("ไม่พบข้อมูล", "ไม่พบข้อมูลตามเงื่อนไขที่เลือก", "warning");
          }
        })
        .catch(function () {
          Swal.fire("เกิดข้อผิดพลาด", "ไม่สามารถดาวน์โหลดไฟล์ได้", "error");
        })
        .then(function () {
          self.isExporting = false;
          KTApp.unblockPage();
        });
    },

    formatNum: function (v) {
      if (v === null || v === undefined || v === "") return "";
      return Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatScvd: function (v) {
      if (v === null || v === undefined || v === "" || v === "-") return "-";
      return Number(v).toFixed(0);
    },
    // J — พื้นหลังคอลัมน์สถานะตามสีของระดับเกณฑ์ (ColorHex). ไม่มีสถานะ → ไม่ระบายสี.
    statusStyle: function (r) {
      if (!r || !r.colorHex) return {};
      return { backgroundColor: r.colorHex };
    },
    // K — สีตัวอักษรคอลัมน์แนวทางแก้ไข ตามสีของระดับเกณฑ์ (ColorHex) เดียวกับ J · ไม่มีพื้นหลัง. ไม่มีสถานะ → สีปกติ.
    actionStyle: function (r) {
      if (!r || !r.colorHex) return {};
      return { color: r.colorHex };
    },
  },
};
</script>

<style scoped>
.scd-report {
  min-width: 1100px;
}
.scd-report th,
.scd-report td {
  padding: 0.35rem 0.5rem;
  vertical-align: middle;
  white-space: nowrap;
}
.scd-report thead th {
  background-color: #d9ead3;
  text-align: center;
  vertical-align: middle;
}
/* คอลัมน์สถานะ (J) — พื้นหลังตามสีของระดับเกณฑ์ (inline style) */
.scd-status-cell {
  font-weight: 600;
}
.scd-total-row td {
  background-color: #f2f2f2;
  font-weight: 600;
}
.scd-grand-total-row td {
  background-color: #d0cece;
  font-weight: 700;
}
</style>



// WEBPACK FOOTER //
// App/Components/StockCoverDays/StockCoverDaysReport.vue